import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { SnackbarContext } from '../App';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
    Briefcase, Building, MapPin, Layers,
    RefreshCw, Pencil, Save, ExternalLink,
    ClipboardList, ChevronLeft, Ghost
} from "lucide-react";
import { format, formatDistanceToNow } from 'date-fns';
import { getStageBadge, getVerificationIcon } from '../utils/crmHelpers';

export default function ContactDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    
    const [contactDetail, setContactDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editForm, setEditForm] = useState({});
    
    // Auxiliary data mapping needs
    const [workspaceCompanies, setWorkspaceCompanies] = useState([]);

    const fetchWorkspaceCompanies = async () => {
        try {
            const res = await api.get('/api/crm/companies?limit=1000');
            setWorkspaceCompanies(res.data.data || []);
        } catch(e) {
            console.error(e);
        }
    }

    const fetchContactDetail = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/api/contacts/${id}`);
            const data = res.data;
            setContactDetail(data);
            setEditForm({
                stage: data.stage || 'LEAD',
                tags: (data.tags || []).join(', '),
                notes: data.notes || '',
                overrideEmail: data.overrideEmail || '',
                companyName: data.companyName || ''
            });
        } catch (err) {
            console.error('Failed to fetch contact detail:', err);
            showSnackbar(err.response?.data?.message || 'Failed to fetch detail', 'error');
            navigate('/crm/contacts');
        } finally {
            setIsLoading(false);
        }
    }, [id, showSnackbar, navigate]);

    useEffect(() => {
        if (id) {
            fetchContactDetail();
            fetchWorkspaceCompanies();
        }

    }, [id, fetchContactDetail]);

    const handleReverify = async () => {
        if (!contactDetail) return;
        setIsSaving(true);
        try {
            const res = await api.post(`/api/contacts/${contactDetail.id}/reverify`);
            setContactDetail(res.data);
            showSnackbar('Enrichment and Re-verification started', 'success');
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Verification kick-off failed', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveContactEdit = async () => {
        setIsSaving(true);
        try {
            const updates = { ...editForm, tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
            if (!updates.companyName) updates.companyId = ""; 

            const res = await api.put(`/api/contacts/${contactDetail.id}`, updates);
            setContactDetail(res.data);
            setIsEditing(false);
            showSnackbar('Contact updated successfully', 'success');
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to update contact', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading || !contactDetail) {
        return (
            <div className="flex-1 p-8 h-full flex flex-col items-center justify-center bg-muted/10">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-muted-foreground font-medium animate-pulse">Loading Contact Profile...</p>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-muted/5 min-h-screen pb-20">
            {/* Top Navigation Strip */}
            <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border p-4 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/crm/contacts')} className="h-8 text-xs font-semibold uppercase tracking-widest text-muted-foreground/80 hover:text-foreground">
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back to Contacts
                    </Button>
                </div>
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={isSaving} className="h-9">
                                Cancel
                            </Button>
                            <Button size="sm" onClick={handleSaveContactEdit} disabled={isSaving} className="h-9 bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                                <Save className="w-4 h-4 mr-2" /> {isSaving ? 'Saving...' : 'Save Profile'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="outline" size="sm" className="h-9 bg-background/90 backdrop-blur" onClick={() => setIsEditing(true)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit Profile
                        </Button>
                    )}
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
                
                {/* Header Banner - Full Width Card */}
                <div className="bg-card border border-border/80 shadow-md rounded-2xl overflow-hidden relative">
                    <div className="h-32 bg-gradient-to-r from-indigo-500/20 via-primary/10 to-transparent w-full absolute top-0 left-0"></div>
                    <div className="p-8 relative z-10 mt-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="flex items-end gap-6">
                            <div className="w-24 h-24 rounded-3xl bg-indigo-50 border border-indigo-200 shadow-xl flex items-center justify-center text-indigo-700 font-extrabold text-5xl uppercase">
                                {(contactDetail.firstName || contactDetail.displayName || 'U').substring(0, 1)}
                            </div>
                            <div className="space-y-1.5 pb-2">
                                <h1 className="text-4xl font-black tracking-tight text-foreground">{contactDetail.displayName}</h1>
                                <p className="text-xl text-muted-foreground font-medium flex items-center gap-2">
                                    <Briefcase className="w-5 h-5 text-primary/70" /> {contactDetail.title || 'Unknown Title'}
                                    {contactDetail.companyName && (
                                        <>
                                            <span className="opacity-30 mx-1">•</span>
                                            <Building className="w-5 h-5 text-primary/70" /> {contactDetail.companyName}
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="pb-2 flex flex-col md:items-end gap-2">
                            <div className="text-sm font-semibold flex items-center gap-2 bg-background p-2 px-4 rounded-xl border shadow-sm w-fit">
                                {getVerificationIcon(contactDetail.verificationStatus)}
                                {contactDetail.displayEmail}
                                {contactDetail.overrideEmail && <Badge variant="secondary" className="text-[10px] h-4 ml-1">OVERRIDE</Badge>}
                            </div>
                            {contactDetail.isStale && <Badge variant="destructive" className="ml-auto animate-pulse">STALE (Outdated data)</Badge>}
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* LEFT COLUMN: IDENTITIES & CRM DATA */}
                    <div className="space-y-6">
                        {/* CRM Meta */}
                        <div className="bg-card p-6 border border-border/70 rounded-2xl shadow-sm space-y-5 relative">
                            <div className="absolute top-0 right-0 max-w-[150px] bg-primary/10 text-[9px] text-primary font-black px-3 py-1 rounded-bl-xl rounded-tr-2xl tracking-wider">
                                WORKSPACE CRM
                            </div>
                            <div className="space-y-2 pt-2">
                                <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Stage</span>
                                {isEditing ? (
                                    <Select value={editForm.stage} onValueChange={(val) => setEditForm({ ...editForm, stage: val })}>
                                        <SelectTrigger className="h-9">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="LEAD">Lead</SelectItem>
                                            <SelectItem value="CONTACTED">Contacted</SelectItem>
                                            <SelectItem value="REPLIED">Replied</SelectItem>
                                            <SelectItem value="MEETING_BOOKED">Meeting</SelectItem>
                                            <SelectItem value="CLOSED_WON">Won</SelectItem>
                                            <SelectItem value="CLOSED_LOST">Lost</SelectItem>
                                            <SelectItem value="UNQUALIFIED">Unqualified</SelectItem>
                                        </SelectContent>
                                    </Select>
                                ) : <div className="pt-1 text-base">{getStageBadge(contactDetail.stage)}</div>}
                            </div>
                            <div className="space-y-2">
                                <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Account</span>
                                {isEditing ? (
                                    <div className="relative">
                                        <Input
                                            value={editForm.companyName}
                                            onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                                            placeholder={contactDetail.companyName || "Company Name..."}
                                            list="workspace-company-datalist"
                                        />
                                        <datalist id="workspace-company-datalist">
                                            {workspaceCompanies.map(c => <option key={c._id} value={c.name} />)}
                                        </datalist>
                                    </div>
                                ) : (
                                    <span className="text-base font-semibold block">{contactDetail.companyName || <span className="opacity-50 italic font-normal">No Company Linked</span>}</span>
                                )}
                            </div>
                            <div className="space-y-2">
                                <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Override Email</span>
                                {isEditing ? (
                                    <Input
                                        value={editForm.overrideEmail}
                                        onChange={(e) => setEditForm({ ...editForm, overrideEmail: e.target.value })}
                                        placeholder={contactDetail.canonicalEmail}
                                    />
                                ) : (
                                    <span className="text-base font-medium block">{contactDetail.overrideEmail || <span className="text-muted-foreground italic">- None -</span>}</span>
                                )}
                            </div>
                            <div className="space-y-2">
                                <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Tags</span>
                                {isEditing ? (
                                    <Input
                                        value={editForm.tags}
                                        onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                                        placeholder="tag1, tag2"
                                    />
                                ) : (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {contactDetail.tags?.length > 0 ? (
                                            contactDetail.tags.map((t, idx) => <Badge key={idx} variant="outline" className="border-primary/20 bg-primary/5 text-primary text-xs">{t}</Badge>)
                                        ) : <span className="text-muted-foreground text-sm">-</span>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Enrichment Meta */}
                        <div className="bg-muted/40 p-6 border border-border/50 rounded-2xl relative">
                            <div className="absolute top-0 right-0 max-w-[150px] bg-slate-500/10 text-[9px] text-slate-500 font-black px-3 py-1 rounded-bl-xl rounded-tr-2xl tracking-wider">
                                GLOBAL CANONICAL
                            </div>
                            <div className="space-y-5 pt-2">
                                <div className="space-y-1">
                                    <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Master Identity</span>
                                    <div className="flex items-center justify-between group bg-background p-2 rounded border shadow-sm">
                                        <span className="font-semibold text-sm">{contactDetail.canonicalEmail || '-'}</span>
                                        <Button size="icon" variant="ghost" className="h-6 w-6 opacity-40 group-hover:opacity-100 hover:bg-emerald-500/10 hover:text-emerald-600" onClick={handleReverify} disabled={isSaving} title="Force Re-enrich">
                                            <RefreshCw className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                                        </Button>
                                    </div>
                                    {contactDetail.lastVerifiedAt && (
                                        <div className="text-[10px] text-muted-foreground mt-1 text-right w-full block">
                                            Verified by {contactDetail.verificationSource || 'API'} · {formatDistanceToNow(new Date(contactDetail.lastVerifiedAt))} ago
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Location</span>
                                    <span className="flex items-center gap-2 text-sm font-medium"><MapPin className="w-4 h-4 text-muted-foreground" /> {contactDetail.location || '-'}</span>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">LinkedIn & Enrichment</span>
                                    <div className="flex flex-col gap-2 mt-2">
                                        {contactDetail.linkedinUrl ? 
                                            <a href={contactDetail.linkedinUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 py-1.5 rounded-md font-bold text-xs border border-blue-200 transition-colors">
                                                <ExternalLink className="w-3.5 h-3.5"/> View Profile
                                            </a> 
                                            : <span className="text-sm">-</span>
                                        }
                                        {!contactDetail.isStale && contactDetail.enrichmentSource && <Badge variant="outline" className="text-emerald-600 border-emerald-300 w-fit mt-1 justify-center">{contactDetail.enrichmentSource} (FRESH)</Badge>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: INSIGHTS & ENGAGEMENT */}
                    <div className="md:col-span-2 space-y-6">
                        
                        {/* Engagement Stats row */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-card p-4 rounded-xl border border-border/80 shadow-sm flex flex-col justify-center text-center items-center">
                                <span className="text-muted-foreground text-[10px] font-black uppercase tracking-widest mb-2">Total Opens</span>
                                <span className="text-4xl font-black text-amber-500 drop-shadow-sm">{contactDetail.openCount || 0}</span>
                            </div>
                            <div className="bg-card p-4 rounded-xl border border-border/80 shadow-sm flex flex-col justify-center text-center items-center">
                                <span className="text-muted-foreground text-[10px] font-black uppercase tracking-widest mb-2">Total Clicks</span>
                                <span className="text-4xl font-black text-purple-600 drop-shadow-sm">{contactDetail.clickCount || 0}</span>
                            </div>
                            <div className="bg-card p-4 rounded-xl border border-border/80 shadow-sm flex flex-col col-span-2 relative">
                                <span className="text-muted-foreground text-[10px] font-black uppercase tracking-widest mb-3">Enrolled Lists</span>
                                <div className="flex flex-wrap gap-2">
                                    {contactDetail.lists?.length > 0 ? (
                                        contactDetail.lists.map(list => (
                                            <div key={list._id} className="text-xs font-bold border border-primary/20 bg-primary/5 text-primary rounded-lg px-3 py-1.5 hover:bg-primary/10 cursor-pointer transition-colors" onClick={() => navigate(`/crm/lists/${list._id}`)}>
                                                <ClipboardList className="w-4 h-4 mr-1.5 mb-0.5 inline" /> {list.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center opacity-40">
                                            <span className="text-sm font-semibold italic">Not assigned to any list</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* CRM Notes block */}
                        <div className="bg-card p-6 border border-border/70 rounded-2xl shadow-sm space-y-3">
                            <span className="text-foreground flex items-center gap-2 text-sm font-black uppercase tracking-widest">
                                <Pencil className="w-4 h-4 text-primary" /> CRM Notes
                            </span>
                            {isEditing ? (
                                <Textarea
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                    placeholder="Add notes for this contact..."
                                    className="min-h-[120px] text-base"
                                />
                            ) : (
                                <div className="bg-muted/20 border border-border/50 p-4 rounded-xl text-base text-foreground min-h-[100px] whitespace-pre-wrap leading-relaxed">
                                    {contactDetail.notes || <span className="italic text-muted-foreground/60 text-sm">No notes provided for this prospect. Hit Edit Profile to add one.</span>}
                                </div>
                            )}
                        </div>

                        {/* Sequence Visibility block */}
                        {contactDetail.sequences && (
                            <div className="bg-card border border-border/70 shadow-sm rounded-2xl p-6 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1.5 h-full bg-primary/60"></div>
                                <h4 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 mb-6 text-foreground/90">
                                    <Layers className="w-5 h-5 text-primary" />
                                    Active Sequence Footprint
                                </h4>
                                
                                {contactDetail.sequences.length === 0 ? (
                                    <div className="bg-muted/10 border border-dashed border-border/60 rounded-xl p-8 text-center flex flex-col items-center justify-center opacity-70">
                                        <Ghost className="w-8 h-8 text-muted-foreground mb-3" />
                                        <p className="text-sm text-foreground font-medium">This contact has no active or historical sequence traces.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {contactDetail.sequences.map((seqAssign, idx) => (
                                            <div key={idx} className="flex flex-col md:flex-row md:items-center justify-between gap-4 border border-border/50 bg-muted/10 p-4 rounded-xl hover:bg-muted/20 transition-colors">
                                                <div className="flex flex-col">
                                                    <span className="text-base font-bold text-foreground cursor-pointer hover:underline" onClick={() => navigate(`/sequences/${seqAssign.sequenceId}`)}>
                                                        {seqAssign.sequenceName}
                                                    </span>
                                                    <span className="text-xs font-semibold text-muted-foreground mt-1">Step {seqAssign.currentStepNumber} of {seqAssign.totalSteps}</span>
                                                </div>
                                                <div className="flex flex-col items-start md:items-end gap-1.5">
                                                    <Badge variant="outline" className={`text-xs px-3 h-6 font-bold tracking-widest uppercase ${seqAssign.assignmentStatus === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' :
                                                        seqAssign.assignmentStatus === 'BOUNCED' ? 'bg-destructive/10 text-destructive border-destructive/30' :
                                                            seqAssign.assignmentStatus === 'REPLIED' ? 'bg-primary/10 text-primary border-primary/30' :
                                                                seqAssign.assignmentStatus === 'FAILED' ? 'bg-amber-500/10 text-amber-600 border-amber-500/30' :
                                                                    'bg-accent text-accent-foreground border-border'
                                                        }`}>{seqAssign.assignmentStatus}</Badge>
                                                        
                                                    <span className="text-[10px] font-bold text-muted-foreground/80 flex items-center gap-1.5 mt-1 uppercase tracking-widest">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-primary/40"></div>
                                                        {['ACTIVE', 'PENDING', 'DEFERRED'].includes(seqAssign.assignmentStatus)
                                                            ? `Next: ${seqAssign.nextStepScheduledAt ? formatDistanceToNow(new Date(seqAssign.nextStepScheduledAt), { addSuffix: true }) : 'Immediate'}`
                                                            : `Execution Finished`
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        
                    </div>
                </div>
                
                {/* Micro Footer */}
                <div className="pt-8 pb-4 border-t border-border/40 flex items-center justify-between text-xs font-semibold text-muted-foreground/60 uppercase tracking-widest">
                    <span>Origin: <span className="font-bold text-muted-foreground/80">{contactDetail.id}</span></span>
                    <span>Profile Extracted: {contactDetail.createdAt ? format(new Date(contactDetail.createdAt), 'MMM dd, yyyy') : 'Unknown'}</span>
                </div>
            </div>
        </div>
    );
}
